import React from "react";
import { FaSun, FaMoon } from "react-icons/fa";
import { useTheme } from "../../context/ThemeContext";

export default function AppearanceTab() {
  const { theme, setTheme } = useTheme();

  const options = [
    {
      key: "light",
      label: "Light",
      description: "Clean white interface with light backgrounds",
      icon: FaSun,
      preview: { bg: "#F6F8FA", card: "#FFFFFF", sidebar: "#0F1117", accent: "#0171DE" },
    },
    {
      key: "dark",
      label: "Dark",
      description: "Easy on the eyes with dark backgrounds",
      icon: FaMoon,
      preview: { bg: "#0F1117", card: "#1A1D2B", sidebar: "#0B0D13", accent: "#0171DE" },
    },
  ];

  const activeOption = options.find((o) => o.key === theme) || options[0];

  return (
    <div>
      <h2 className="text-lg font-semibold text-ink-primary mb-1">Appearance</h2>
      <p className="text-sm text-ink-muted mb-6">
        Choose how Walytic looks for you. Select a theme below.
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
        {/* Theme swatches */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {options.map((opt) => {
            const isActive = theme === opt.key;
            const Icon = opt.icon;
            return (
              <button
                key={opt.key}
                onClick={() => setTheme(opt.key)}
                className={`text-left rounded-xl border-2 p-4 transition-all ${
                  isActive
                    ? "border-brand ring-2 ring-brand/20"
                    : "border-surface-border hover:border-ink-faint"
                }`}
              >
                {/* Mini preview */}
                <div
                  className="rounded-lg overflow-hidden mb-3 border border-surface-border"
                  style={{ background: opt.preview.bg }}
                >
                  <div className="flex h-[80px]">
                    <div
                      className="w-[40px] flex-shrink-0"
                      style={{ background: opt.preview.sidebar }}
                    />
                    <div className="flex-1 p-2">
                      <div
                        className="h-3 w-16 rounded mb-1.5"
                        style={{ background: opt.preview.accent }}
                      />
                      <div className="flex gap-1.5">
                        <div
                          className="h-10 flex-1 rounded"
                          style={{ background: opt.preview.card }}
                        />
                        <div
                          className="h-10 flex-1 rounded"
                          style={{ background: opt.preview.card }}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2 mb-1">
                  <Icon
                    size={14}
                    className={isActive ? "text-brand" : "text-ink-muted"}
                  />
                  <span className="text-sm font-semibold text-ink-primary">
                    {opt.label}
                  </span>
                </div>
                <p className="text-xs text-ink-muted">{opt.description}</p>
              </button>
            );
          })}
        </div>

        {/* Live preview pane */}
        <aside className="rounded-xl border border-surface-border bg-surface-card p-4 h-fit lg:sticky lg:top-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-ink-primary">Live preview</h3>
            <span className="text-[11px] uppercase tracking-wide text-ink-muted">
              {activeOption.label}
            </span>
          </div>
          <div
            className="rounded-lg overflow-hidden border border-surface-border"
            style={{ background: activeOption.preview.bg }}
          >
            <div className="flex h-[200px]">
              <div
                className="w-[56px] flex-shrink-0 flex flex-col items-center gap-2 py-3"
                style={{ background: activeOption.preview.sidebar }}
              >
                <div
                  className="h-6 w-6 rounded-md"
                  style={{ background: activeOption.preview.accent }}
                />
                <div className="h-2 w-6 rounded" style={{ background: "#ffffff22" }} />
                <div className="h-2 w-6 rounded" style={{ background: "#ffffff14" }} />
                <div className="h-2 w-6 rounded" style={{ background: "#ffffff14" }} />
              </div>
              <div className="flex-1 p-3">
                <div
                  className="h-3 w-24 rounded mb-2"
                  style={{ background: activeOption.preview.accent }}
                />
                <div
                  className="h-2 w-32 rounded mb-3"
                  style={{ background: activeOption.preview.card, opacity: 0.7 }}
                />
                <div className="grid grid-cols-2 gap-2">
                  <div
                    className="h-16 rounded"
                    style={{ background: activeOption.preview.card }}
                  />
                  <div
                    className="h-16 rounded"
                    style={{ background: activeOption.preview.card }}
                  />
                  <div
                    className="h-10 rounded col-span-2"
                    style={{ background: activeOption.preview.card }}
                  />
                </div>
              </div>
            </div>
          </div>
          <p className="text-xs text-ink-muted mt-3">
            Updates apply instantly across the app.
          </p>
        </aside>
      </div>
    </div>
  );
}
